/* eslint-disable react/jsx-no-bind */
import classNames from "classnames";
import Badge from "~/components/shared/Badge";
import { NavLink, useMatches } from "react-router-dom";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types/Build";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { useWaterfallAvailableStore } from "~/components/build/Show/lib/useWaterfallAvailableStore";
import {
  ANNOTATIONS_VIEWS,
  BuildView,
  STEPS_DISPLAY_MODES,
  SUMMARY_DISPLAY_MODES,
  TESTS_VIEWS,
  resolveStepsDisplayMode,
} from "~/components/build/Show/lib/types/BuildView";
import { urlForView } from "~/components/build/Show/lib/urlForView";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useMemo } from "react";
import FailedTestExecutionsIndicator from "~/components/job/Job/FailedTestExecutionsIndicator";
import { motion } from "framer-motion";
import Count from "~/components/build/Show/components/Count";
import AnnotationsPanelToggle from "~/components/build/Show/components/AnnotationsPanelToggle";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useMobileBreakpoint } from "~/components/build/Show/lib/useBreakpoints";

const BuildNavLink = ({
  children,
  activeViews,
  url,
}: {
  children: React.ReactNode;
  activeViews: ReadonlySet<BuildView>;
  url: string;
}) => {
  const matches = useMatches();
  const isActive = matches.some((match) => activeViews.has(match.id as BuildView));

  return (
    <NavLink
      to={url}
      className="btn btn-tab relative px-2 text-sm text-current transition-colors focus:text-current"
    >
      {children}
      {isActive && (
        <motion.div
          layoutId="build-nav-link-active-indicator"
          className="absolute left-0 right-0 top-full h-[3px] bg-purple-600"
          transition={{ type: "spring", duration: 0.2, bounce: 0 }}
        />
      )}
    </NavLink>
  );
};

export function showTestsTabPill(build: Build) {
  // The "new" pill is cleared when the tests tab turbo frame is loaded in
  // the V2TestEngine component.
  const isMarketingUrl = build.testEngine.testTabUrls?.tabUrl?.includes("marketing");
  const isPillDismissed = localStorage.getItem("hide-test-tab-pill") === "true";
  return isMarketingUrl && !isPillDismissed;
}

function useStepsView() {
  const currentModeId = useBuildStore((state) => state.stepsDisplayMode);
  return resolveStepsDisplayMode(currentModeId);
}

function BuildContentNavigation() {
  const { build } = useBuild();
  const params = useQueryParams();
  const stepsView = useStepsView();
  const isMobile = useMobileBreakpoint();

  const navItems = useMemo(() => {
    const items: {
      label: string;
      url: string;
      activeViews: ReadonlySet<BuildView>;
      indicator?: React.ReactNode;
      pill?: React.ReactNode;
      count?: React.ReactNode;
    }[] = [
      {
        label: "Summary",
        url: urlForView(build.path, BuildView.Summary, params),
        activeViews: SUMMARY_DISPLAY_MODES,
      },
    ];

    if (Features.BuildPageDockedAnnotations && !isMobile) {
      // Individual top-level tabs for each step view
      if (Features.BuildPageListView) {
        items.push({
          label: "List",
          url: urlForView(build.path, BuildView.List, params),
          activeViews: new Set([BuildView.List]),
        });
      }

      items.push({
        label: "Table",
        url: urlForView(build.path, BuildView.Table, params),
        activeViews: new Set([BuildView.Table]),
      });

      if (!Features.DisableStepsCanvasView) {
        items.push({
          label: "Canvas",
          url: urlForView(build.path, BuildView.Canvas, params),
          activeViews: new Set([BuildView.Canvas]),
        });
      }

      if (!Features.DisableStepsWaterfallView) {
        items.push({
          label: "Waterfall",
          url: urlForView(build.path, BuildView.Waterfall, params),
          activeViews: new Set([BuildView.Waterfall]),
        });
      }
    } else {
      // Single "Steps" tab that expands into the SubNavBar in StepsLayout
      items.push({
        label: "Steps",
        url: urlForView(build.path, stepsView, params),
        activeViews: STEPS_DISPLAY_MODES,
      });
    }

    if (!Features.BuildPageDockedAnnotations) {
      const counts = getAnnotationCounts(build);
      const annotationCount = counts.totalCount;

      items.push({
        url: urlForView(build.path, BuildView.Annotations, params),
        activeViews: ANNOTATIONS_VIEWS,
        label: "Annotations",
        count: annotationCount > 0 && (
          <span className="ml-1">
            <Count data-testid="annotations-count">{annotationCount}</Count>
          </span>
        ),
      });
    }

    if (build.testEngine.testTabUrls?.tabUrl && !Features.TestEngineDisableBuildPageTestTab) {
      items.push({
        label: "Tests",
        url: urlForView(build.path, BuildView.Tests, params),
        activeViews: TESTS_VIEWS,
        indicator: (
          <FailedTestExecutionsIndicator build={build} translateX="1px" translateY="8px" />
        ),
        pill: showTestsTabPill(build) && (
          <span className="badge badge--outline tabular-numerals ml-1">New</span>
        ),
      });
    }

    return items;
  }, [build, isMobile, params, stepsView]);

  if (!Features.BuildSidebar) {
    return <ClassicNavigation />;
  }

  return (
    <nav data-testid="BuildContentNavigation" className="flex flex-auto items-center">
      {navItems.map(({ label, url, activeViews, indicator, pill, count }) => (
        <BuildNavLink key={label} url={url} activeViews={activeViews}>
          {indicator}
          {label}
          {pill}
          {count}
        </BuildNavLink>
      ))}
      {Features.BuildPageDockedAnnotations && (
        <div className="ml-auto flex items-center">
          <AnnotationsPanelToggle />
        </div>
      )}
    </nav>
  );
}

/**
 * Navigation for users without BuildSidebar feature
 */
function ClassicNavigation() {
  const { build } = useBuild();
  const waterfallAvailable = useWaterfallAvailableStore((state) => state.waterfallAvailable);

  const listHref = build.path;
  const testsHref = "./tests";
  const canvasHref = "./canvas";
  const waterfallHref = "./waterfall";
  const showBuildTests =
    build.testEngine.testTabUrls?.tabUrl && !Features.TestEngineDisableBuildPageTestTab;

  return (
    <nav className="Tablist border-gray mb-[-1px] flex border-b" data-testid="buildViewTabsNav">
      <NavLink
        to={listHref}
        end={true}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Jobs</span>
      </NavLink>

      <NavLink
        to={canvasHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Canvas</span>
      </NavLink>

      <NavLink
        to={waterfallHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Waterfall</span>
        {!waterfallAvailable && (
          <Badge outline={false} className="white bg-purple">
            Upgrade
          </Badge>
        )}
      </NavLink>

      {showBuildTests && (
        <NavLink
          to={testsHref}
          className={({ isActive }) =>
            classNames("btn btn-tab relative text-current focus:text-current", {
              "btn-tab--active": isActive,
            })
          }
        >
          <FailedTestExecutionsIndicator build={build} />
          <span>Tests</span>
          {showTestsTabPill(build) && (
            <span className="badge badge--outline tabular-numerals ml-1">New</span>
          )}
        </NavLink>
      )}
    </nav>
  );
}

export default BuildContentNavigation;
